import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.07 — Area Validation Screen.
 * Displays measured vs approved area comparison.
 */
interface AreaValidationResult {
  readonly verificationId: string;
  readonly jobRequestId: string;
  readonly approvedAreaSqft: number | null;
  readonly measuredAreaSqft: number | null;
  readonly variancePct: number | null;
  readonly tolerancePct: number;
  readonly status: string;
  readonly validatedAt: string | null;
  readonly remarks: string | null;
}

export function AreaValidationPage(): ReactNode {
  const { t } = useTranslation();
  const { number } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [data, setData] = useState<AreaValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<AreaValidationResult>(`/v1/closure/review/${verificationId}/area`, { token: auth.token })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load area validation'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="area-validation"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!data) return <main aria-label="area-validation"><p>{t('closure.area.loading')}</p></main>;

  const withinTolerance = data.variancePct != null && Math.abs(data.variancePct) <= data.tolerancePct;

  return (
    <main aria-label="area-validation">
      <h2>{t('closure.area.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.area.back')}</Link></p>
      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 500 }}>
        <tbody>
          <tr><th style={th}>{t('closure.area.approved')}</th><td style={td}>{data.approvedAreaSqft != null ? t('mob.closure.area.sqftValue', { value: number(data.approvedAreaSqft, 1) }) : '—'}</td></tr>
          <tr><th style={th}>{t('closure.area.measured')}</th><td style={td}>{data.measuredAreaSqft != null ? t('mob.closure.area.sqftValue', { value: number(data.measuredAreaSqft, 1) }) : '—'}</td></tr>
          <tr><th style={th}>{t('closure.area.variance')}</th><td style={td}>{data.variancePct != null ? `${number(data.variancePct, 1)}%` : '—'}</td></tr>
          <tr><th style={th}>{t('closure.area.tolerance')}</th><td style={td}>±{number(data.tolerancePct)}%</td></tr>
          <tr>
            <th style={th}>{t('closure.area.result')}</th>
            <td style={{ ...td, color: withinTolerance ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 'bold' }}>
              {withinTolerance ? t('closure.area.within') : data.variancePct != null ? t('closure.area.exceeded') : t('closure.area.notValidated')}
            </td>
          </tr>
          <tr><th style={th}>{t('closure.area.status')}</th><td style={td}>{data.status}</td></tr>
          <tr><th style={th}>{t('closure.area.validatedAt')}</th><td style={td}>{at(data.validatedAt)}</td></tr>
          {data.remarks && <tr><th style={th}>{t('closure.area.remarks')}</th><td style={td}>{data.remarks}</td></tr>}
        </tbody>
      </table>
    </main>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)', width: 130 };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)' };
